import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Card,
  Select,
  Input,
  Upload,
  Button,
  Table,
  Tag,
  Alert,
  Space,
  Typography,
  App as AntApp,
} from 'antd';
import { InboxOutlined, DownloadOutlined } from '@ant-design/icons';
import apiClient from '@/services/api';
import { useSession } from '@/hooks/useSession';

const { Title, Paragraph, Text } = Typography;

interface Plan {
  uuid: string;
  name: string | null;
  productId: string | null;
  quantity: number | null;
  enabled: boolean;
}
interface LicensesResp { Result: string; count: number; plans: Plan[] }

interface UnitInfo {
  licenseUnitUUID: string;
  deviceUUID: string;
  expired: string | null;
  status: 'active' | 'suspended' | 'expired';
}
interface Skipped { deviceId: string; reason: string }
interface BulkIssueResp {
  Result: string;
  licenses: string[];
  licenseInfo: UnitInfo[];
  skipped: Skipped[];
}

// One DeviceUUID per line; tolerates the --collect-ids CSV (first column, header
// skipped) and dedups.
function parseDeviceIds(text: string): string[] {
  const ids: string[] = [];
  for (let line of text.split(/\r?\n/)) {
    line = line.trim();
    if (!line) continue;
    const first = line.split(',')[0].trim();
    if (/^device_uuid$/i.test(first)) continue;
    ids.push(first);
  }
  return Array.from(new Set(ids));
}

function downloadDat(licenses: string[]) {
  const blob = new Blob([licenses.join('\n') + '\n'], { type: 'application/octet-stream' });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = 'license.dat';
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function VendorBulkIssuePage() {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const { user } = useSession();
  const canManage = user?.role === 'vendor_operator' && !!user?.canSelfManageUnits;

  const [planUuid, setPlanUuid] = useState<string | undefined>();
  const [text, setText] = useState('');
  const [sigAlg, setSigAlg] = useState<'ed25519' | 'rsa'>('ed25519');
  const [result, setResult] = useState<BulkIssueResp | null>(null);

  const deviceIds = useMemo(() => parseDeviceIds(text), [text]);

  const plans = useQuery({
    queryKey: ['vendor', 'portal', 'licenses'],
    queryFn: async () => (await apiClient.get<LicensesResp>('/vendor/portal/licenses')) as unknown as LicensesResp,
  });

  const issueMut = useMutation({
    mutationFn: async () =>
      (await apiClient.post<BulkIssueResp>('/vendor/portal/licenses/bulk-issue', {
        licenseUUID: planUuid,
        deviceIds,
        sigAlg,
      })) as unknown as BulkIssueResp,
    onSuccess: (r) => {
      setResult(r);
      message.success(t('vendorBulk.issued', { n: r.licenses?.length ?? 0 }));
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || e?.response?.data?.Result || String(e);
      message.error(msg);
    },
  });

  return (
    <>
      <Title level={3} style={{ marginTop: 0 }}>{t('vendorBulk.title')}</Title>
      <Paragraph type="secondary">{t('vendorBulk.desc')}</Paragraph>

      {!canManage && (
        <Alert type="warning" showIcon style={{ marginBottom: 16 }} message={t('vendor.notAuthorizedMsg')} />
      )}

      <Card style={{ marginBottom: 16 }}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <div>
            <Text strong>{t('vendorBulk.selectPlan')}</Text>
            <Select
              style={{ width: '100%', marginTop: 6 }}
              placeholder={t('vendorBulk.selectPlan')}
              loading={plans.isLoading}
              value={planUuid}
              onChange={setPlanUuid}
              options={(plans.data?.plans ?? [])
                .filter((p) => p.enabled)
                .map((p) => ({
                  value: p.uuid,
                  label: `${p.name ?? p.uuid}${p.quantity ? ` (max ${p.quantity})` : ''}`,
                }))}
            />
          </div>

          <div>
            <Text strong>{t('vendorBulk.deviceIds')}</Text>
            <Paragraph type="secondary" style={{ margin: '2px 0 6px' }}>{t('vendorBulk.deviceIdsHint')}</Paragraph>
            <Upload.Dragger
              accept=".csv,.txt"
              maxCount={1}
              showUploadList={false}
              beforeUpload={(file) => {
                file.text().then((txt) => setText((prev) => (prev ? prev + '\n' : '') + txt));
                return false; // don't auto-upload; we parse client-side
              }}
              style={{ marginBottom: 8 }}
            >
              <p className="ant-upload-drag-icon"><InboxOutlined /></p>
              <p className="ant-upload-text">{t('vendorBulk.uploadCsv')}</p>
            </Upload.Dragger>
            <Input.TextArea
              rows={6}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={'76F74B3C-4C00-4CBD-9BD3-0D7E3325CB70\n...'}
            />
            <Text type="secondary">{t('vendorBulk.count', { n: deviceIds.length })}</Text>
          </div>

          <div>
            <Text strong style={{ marginRight: 8 }}>{t('vendorBulk.sigAlg')}</Text>
            <Select
              style={{ width: 220 }}
              value={sigAlg}
              onChange={setSigAlg}
              options={[
                { value: 'ed25519', label: 'ed25519 (softReceiver)' },
                { value: 'rsa', label: 'rsa (iOS / legacy)' },
              ]}
            />
          </div>

          <Button
            type="primary"
            loading={issueMut.isPending}
            disabled={!canManage || !planUuid || deviceIds.length === 0}
            onClick={() => { setResult(null); issueMut.mutate(); }}
          >
            {t('vendorBulk.issue')}
          </Button>
        </Space>
      </Card>

      {result && (
        <Card
          title={t('vendorBulk.results')}
          extra={
            <Button
              type="primary"
              icon={<DownloadOutlined />}
              disabled={!result.licenses?.length}
              onClick={() => downloadDat(result.licenses)}
            >
              {t('vendorBulk.downloadDat')}
            </Button>
          }
        >
          <Table
            size="small"
            rowKey="licenseUnitUUID"
            pagination={{ pageSize: 50 }}
            dataSource={result.licenseInfo ?? []}
            columns={[
              {
                title: 'Device',
                dataIndex: 'deviceUUID',
                key: 'dev',
                render: (v: string) => <code style={{ fontSize: 11 }}>{v}</code>,
              },
              {
                title: 'Expiry',
                dataIndex: 'expired',
                key: 'exp',
                render: (v: string | null) => (v ? new Date(v).toLocaleDateString() : '—'),
              },
              {
                title: 'Status',
                dataIndex: 'status',
                key: 'st',
                width: 110,
                render: (s: UnitInfo['status']) => (
                  <Tag color={s === 'active' ? 'green' : s === 'suspended' ? 'orange' : undefined}>{s}</Tag>
                ),
              },
            ]}
          />
          {result.skipped?.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginTop: 12 }}
              message={t('vendorBulk.skipped', { n: result.skipped.length })}
              description={
                <ul style={{ margin: 0, paddingLeft: 18 }}>
                  {result.skipped.map((s) => (
                    <li key={s.deviceId}><code>{s.deviceId}</code> — {s.reason}</li>
                  ))}
                </ul>
              }
            />
          )}
        </Card>
      )}
    </>
  );
}
